/**
 * ST-2 — the walk, on the phone. Scan a blank and it is yours; say which
 * project, or none; photos first, then the words, both saved as you go; close
 * and scan the next one.
 *
 * Chrome-less on purpose: this is opened as a pop-out (420x820) or from the
 * phone's home screen, and it has one job at a time.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import jsQR from 'jsqr';
import { TellSomeoneSheet } from '../../components/TellSomeoneSheet';
import { askWord } from '../../components/AskDesignerSheet';
import '../../styles/site-tags.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const clock = (when) => (when
  ? new Date(when).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  : '');
const day = (when) => (when
  ? new Date(when).toLocaleDateString([], { month: 'short', day: 'numeric' })
  : '');

export default function SiteTagsWidget() {
  const [params] = useSearchParams();
  const [screen, setScreen] = useState('scan');
  // ST-3b — the walk can hand a tag on before it ends.
  const [telling, setTelling] = useState(false);
  const [tag, setTag] = useState(null);
  const [choices, setChoices] = useState([]);
  const [blankLeft, setBlankLeft] = useState(null);
  const [notice, setNotice] = useState('');
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState('');
  const [pending, setPending] = useState(0);
  const [picker, setPicker] = useState('');
  const [cameraOff, setCameraOff] = useState(false);
  // RS-2b — the question the walk can ask from the tag it just placed.
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');
  const [people, setPeople] = useState([]);
  const [who, setWho] = useState('');
  const [withPhotos, setWithPhotos] = useState(true);
  const [askProblem, setAskProblem] = useState('');

  const video = useRef(null);
  const stream = useRef(null);
  const timer = useRef(null);
  const debounce = useRef(null);
  const retry = useRef(null);
  const wanted = useRef('');

  const stopCamera = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (stream.current) {
      stream.current.getTracks().forEach((track) => track.stop());
      stream.current = null;
    }
  }, []);

  const closeWalk = () => {
    stopCamera();
    if (window.opener) window.close();
    else window.location.href = '/site-tags';
  };

  // ---------------------------------------------------------------- lookups
  const openTag = useCallback(async (body) => {
    if (body.kind !== 'spot' || body.scope !== 'firm') {
      setNotice('That is a material tag — open it from the Material Library');
      return;
    }
    if (body.state === 'void') {
      setNotice(`Tag ${body.tag_number} was voided by ${body.voided_by_name || 'the firm'}, `
        + `${day(body.voided_at)}`);
      return;
    }
    if (body.state === 'blank') {
      stopCamera();
      const { data } = await axios.get(`${API}/site-tags/by-number/${body.tag_number}`);
      const list = await axios.get(`${API}/site-tags/project-choices`);
      setTag(data);
      setChoices(list.data.projects || []);
      setScreen('place');
      return;
    }
    stopCamera();
    const { data } = await axios.get(`${API}/site-tags/by-number/${body.tag_number}`);
    setTag(data);
    setNote(data.note || '');
    setScreen('view');
  }, [stopCamera]);

  const lookupCode = useCallback(async (code) => {
    try {
      const { data } = await axios.get(`${API}/material-tags/${code}`);
      await openTag(data);
    } catch {
      setNotice('That tag is not one of ours');
    }
  }, [openTag]);

  const lookupNumber = async () => {
    const number = Number(typed);
    if (!number) return;
    try {
      stopCamera();
      const { data } = await axios.get(`${API}/site-tags/by-number/${number}`);
      setTyped('');
      if (data.state === 'blank') {
        const list = await axios.get(`${API}/site-tags/project-choices`);
        setChoices(list.data.projects || []);
        setTag(data);
        setScreen('place');
      } else {
        setTag(data);
        setNote(data.note || '');
        setScreen('view');
      }
    } catch {
      setNotice(`No tag ${number} on your strips`);
    }
  };

  // ---------------------------------------------------------------- camera
  useEffect(() => {
    if (screen !== 'scan') { stopCamera(); return undefined; }
    let dead = false;
    const canvas = document.createElement('canvas');
    (async () => {
      try {
        const got = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (dead) { got.getTracks().forEach((t) => t.stop()); return; }
        stream.current = got;
        if (video.current) {
          video.current.srcObject = got;
          await video.current.play().catch(() => {});
        }
        timer.current = setInterval(() => {
          const frame = video.current;
          if (!frame || !frame.videoWidth) return;
          canvas.width = frame.videoWidth;
          canvas.height = frame.videoHeight;
          const paper = canvas.getContext('2d');
          paper.drawImage(frame, 0, 0, canvas.width, canvas.height);
          const found = jsQR(paper.getImageData(0, 0, canvas.width, canvas.height).data,
            canvas.width, canvas.height);
          const text = found && found.data ? String(found.data) : '';
          if (!text.includes('/m/')) return;
          const code = text.split('/m/').pop().split(/[?#/]/)[0];
          if (!code || code === wanted.current) return;
          wanted.current = code;
          if (navigator.vibrate) navigator.vibrate(40);
          lookupCode(code);
        }, 120);
      } catch {
        setCameraOff(true);
      }
    })();
    return () => { dead = true; stopCamera(); };
  }, [screen, stopCamera, lookupCode]);

  useEffect(() => {
    axios.get(`${API}/site-tags/counts`)
      .then(({ data }) => setBlankLeft(data.blank)).catch(() => setBlankLeft(null));
  }, [screen]);

  useEffect(() => {
    const code = params.get('code');
    if (code) lookupCode(code);
  }, []);  // once, on the url the phone was opened with

  useEffect(() => {
    if (!notice) return undefined;
    const back = setTimeout(() => { setNotice(''); wanted.current = ''; }, 2000);
    return () => clearTimeout(back);
  }, [notice]);

  useEffect(() => () => {
    stopCamera();
    if (debounce.current) clearTimeout(debounce.current);
    if (retry.current) clearInterval(retry.current);
  }, [stopCamera]);

  // ---------------------------------------------------------------- the walk
  const place = async (projectId) => {
    const { data } = await axios.post(`${API}/site-tags/${tag.id}/place`,
      { project_id: projectId });
    setTag(data);
    setNote('');
    setScreen('capture');
  };

  const patch = useCallback(async (body) => {
    try {
      const { data } = await axios.patch(`${API}/site-tags/${tag.id}`, body);
      setTag(data);
      setSaved(`Saved ${clock(new Date())}`);
      if (retry.current) { clearInterval(retry.current); retry.current = null; }
    } catch {
      setSaved('Could not save — kept on this phone, retrying');
      if (!retry.current) retry.current = setInterval(() => patch(body), 5000);
    }
  }, [tag]);

  const typeNote = (text) => {
    setNote(text);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => patch({ note: text }), 600);
  };

  const sendPhoto = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setPending((n) => n + 1);
    try {
      await axios.post(`${API}/site-tags/${tag.id}/photos`, form);
      const { data } = await axios.get(`${API}/site-tags/${tag.id}`);
      setTag(data);
      setSaved(`Saved ${clock(new Date())}`);
    } catch {
      setSaved('Could not save — kept on this phone, retrying');
    } finally {
      setPending((n) => Math.max(0, n - 1));
    }
  };

  // RS-2b — ask the designer, from the phone, about the tag in hand.
  const openAsk = async () => {
    setQuestion(tag.title || `Tag ${tag.tag_number}`);
    setAskProblem('');
    setAsking(true);
    try {
      const { data } = await axios.get(
        `${API}/projects/${tag.project_id}/people-who-answer`);
      setPeople(data.people || []);
      setWho(data.default_user_id || (data.people || [])[0]?.id || '');
    } catch {
      setAskProblem('Could not read who can answer');
    }
  };

  const sendAsk = async () => {
    if (!question.trim()) { setAskProblem('Write the question first'); return; }
    if (!who) { setAskProblem('Say who answers it'); return; }
    try {
      const { data } = await axios.post(`${API}/site-tags/${tag.id}/ask`, {
        question: question.trim(), assigned_to: who, include_photos: withPhotos,
      });
      setTag(data);
      setAsking(false);
    } catch (e) {
      setAskProblem(e?.response?.data?.detail || 'That question did not send');
    }
  };

  const again = () => {
    setTag(null);
    setNote('');
    setSaved('');
    wanted.current = '';
    setScreen('scan');
  };

  const shutter = (testid) => (
    <label className="kstw-shutter" data-testid={testid}>
      Take a photo
      <input type="file" accept="image/*" capture="environment" hidden onChange={sendPhoto} />
    </label>
  );

  // ---------------------------------------------------------------- screens
  return (
    <div className="kstw" data-testid="site-tags-widget">
      {notice ? <p className="kstw-note" data-testid="site-tags-notice">{notice}</p> : null}

      {screen === 'scan' ? (
        <div className="kstw-screen" data-testid="site-tags-scan">
          <p className="kstw-k">Point at a tag</p>
          {cameraOff ? (
            <p className="kstw-muted">No camera here — type the number instead.</p>
          ) : (
            <div className="kstw-lens">
              <video ref={video} playsInline muted className="kstw-video" />
              <span className="kstw-guide" />
            </div>
          )}
          <p className="kstw-k" data-testid="site-tags-left">
            {blankLeft === null ? '' : `${blankLeft} tags left on your strips`}
          </p>
          <div className="kstw-row">
            <input className="kstw-in" type="number" inputMode="numeric"
              placeholder="Type the number" value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') lookupNumber(); }}
              data-testid="site-tags-type-number" />
            <button type="button" className="kstw-btn" onClick={lookupNumber}
              data-testid="site-tags-find">Find</button>
          </div>
          <button type="button" className="kstw-btn" onClick={closeWalk}
            data-testid="site-tags-close">Close</button>
        </div>
      ) : null}

      {screen === 'place' && tag ? (
        <div className="kstw-screen" data-testid="site-tags-place">
          <p className="kstw-t">Tag {tag.tag_number}</p>
          <p className="kstw-k">Which project is this on?</p>
          <div className="kstw-list">
            {choices.map((choice) => (
              <button type="button" key={choice.id} className="kstw-choice"
                onClick={() => place(choice.id)} data-testid={`site-tags-choice-${choice.id}`}>
                <span className="kstw-choice-n">{choice.name}</span>
                {choice.why ? <span className="kstw-k">{choice.why}</span> : null}
              </button>
            ))}
            <button type="button" className="kstw-choice" onClick={() => place(null)}
              data-testid="site-tags-choice-none">
              <span className="kstw-choice-n">No project yet — firm tag, link later</span>
            </button>
          </div>
          <button type="button" className="kstw-btn" onClick={again}
            data-testid="site-tags-back">Back to scanning</button>
        </div>
      ) : null}

      {screen === 'capture' && tag ? (
        <div className="kstw-screen" data-testid="site-tags-capture">
          <p className="kstw-t">Tag {tag.tag_number} · Placed</p>
          <p className="kstw-k">
            {`${tag.project_name || 'no project yet'} · placed by ${tag.placed_by_name} · `
              + `${clock(tag.placed_at)}`}
          </p>
          {shutter('site-tags-photo')}
          <div className="kstw-thumbs">
            {(tag.photos || []).map((photo, index) => (
              <img key={photo.url} src={photo.url} alt="" className="kstw-thumb"
                data-testid={`site-tags-thumb-${index}`} />
            ))}
            {pending ? <span className="kstw-thumb kstw-thumb--wait" /> : null}
          </div>
          <textarea className="kstw-area" value={note} rows={5}
            placeholder="What's wrong, in your words…"
            onChange={(e) => typeNote(e.target.value)}
            onBlur={() => patch({ note })}
            data-testid="site-tags-note" />
          <div className="kstw-row">
            <button type="button" className="kstw-chip" onClick={() => setPicker('room')}
              data-testid="site-tags-room">Room · {tag.room || 'pick'}</button>
            <button type="button" className="kstw-chip" onClick={() => setPicker('trade')}
              data-testid="site-tags-trade">Trade · {tag.trade || 'pick'}</button>
          </div>
          <p className="kstw-k" data-testid="site-tags-saved">{saved}</p>
          <button type="button" className="kstw-btn kstw-btn--brand"
            onClick={() => setScreen('done')} data-testid="site-tags-close">Close</button>
        </div>
      ) : null}

      {screen === 'done' && tag ? (
        <div className="kstw-screen" data-testid="site-tags-done">
          <p className="kstw-t">Scan another tag?</p>
          <button type="button" className="kstw-btn kstw-btn--brand kstw-wide" onClick={again}
            data-testid="site-tags-again">Yes — scan</button>
          {/* ST-3b — before the walk ends, hand it to whoever must act. */}
          <button type="button" className="kstw-btn kstw-wide"
            onClick={() => setTelling(true)} data-testid="site-tags-tell">
            Tell someone first
          </button>
          {/* RS-2b — or ask the designer; the answer comes back on the tag. */}
          {tag.rfi ? (
            <p className="kstw-k" data-testid="site-tags-rfi">
              {`${tag.rfi.rfi_number} · ${askWord(tag.rfi.status)}`}
              {tag.rfi.answer ? ` — ${tag.rfi.answer}` : ''}
            </p>
          ) : (
            <button type="button" className="kstw-btn kstw-wide"
              disabled={!tag.project_id}
              onClick={openAsk} data-testid="site-tags-ask">
              Ask the designer
            </button>
          )}
          <button type="button" className="kstw-btn kstw-wide" onClick={closeWalk}
            data-testid="site-tags-finish">No — I&apos;m done</button>
          <p className="kstw-k">
            {`Tag ${tag.tag_number} is saved · ${(tag.photos || []).length} photos · `
              + `${tag.trade || 'no trade yet'} · ${tag.project_name || 'no project yet'}`}
          </p>
        </div>
      ) : null}

      {screen === 'view' && tag ? (
        <div className="kstw-screen" data-testid="site-tags-view">
          <p className="kstw-t">{tag.title || `Tag ${tag.tag_number}`}</p>
          <p className="kstw-k">
            {`${tag.room || 'no room'} · ${tag.trade || 'no trade'} · placed by `
              + `${tag.placed_by_name} · ${day(tag.placed_at)}`}
          </p>
          <div className="kstw-thumbs">
            {(tag.photos || []).map((photo, index) => (
              <img key={photo.url} src={photo.url} alt="" className="kstw-thumb"
                data-testid={`site-tags-view-thumb-${index}`} />
            ))}
            {pending ? <span className="kstw-thumb kstw-thumb--wait" /> : null}
          </div>
          <textarea className="kstw-area" value={note} rows={5}
            placeholder="Add to the words…"
            onChange={(e) => setNote(e.target.value)}
            data-testid="site-tags-view-note" />
          {shutter('site-tags-view-photo')}
          <button type="button" className="kstw-btn" onClick={() => patch({ note })}
            data-testid="site-tags-view-save">Add to the words</button>
          <a className="kstw-btn kstw-link" href={`/site-tags/${tag.id}`}
            data-testid="site-tags-open-page">Open the full page</a>
          <p className="kstw-k" data-testid="site-tags-saved">{saved}</p>
          <button type="button" className="kstw-btn" onClick={again}
            data-testid="site-tags-close">Close</button>
        </div>
      ) : null}

      {picker ? (
        <div className="kstw-sheet" data-testid={`site-tags-picker-${picker}`}>
          <p className="kstw-k">{picker === 'room' ? 'Which room?' : 'Which trade?'}</p>
          <div className="kstw-list">
            {(picker === 'room' ? (tag.rooms || []) : (tag.trades || [])).map((row) => {
              const id = picker === 'room' ? row.id : row;
              const label = picker === 'room' ? row.name : row;
              return (
                <button type="button" key={id} className="kstw-choice"
                  onClick={() => {
                    patch(picker === 'room' ? { room_id: id } : { trade: label });
                    setPicker('');
                  }}
                  data-testid={`site-tags-pick-${id}`}>
                  <span className="kstw-choice-n">{label}</span>
                </button>
              );
            })}
            {picker === 'room' && !(tag.rooms || []).length ? (
              <p className="kstw-muted">No rooms on this project yet.</p>
            ) : null}
          </div>
          <button type="button" className="kstw-btn" onClick={() => setPicker('')}
            data-testid="site-tags-picker-close">Never mind</button>
        </div>
      ) : null}

      {asking && tag ? (
        <div className="kstw-sheet" data-testid="site-tags-ask-sheet">
          <p className="kstw-k">Ask the designer</p>
          <textarea className="kstw-area" rows={4} maxLength={2000} value={question}
            onChange={(e) => setQuestion(e.target.value)}
            data-testid="site-tags-ask-question" />
          <select className="kstw-area" value={who} onChange={(e) => setWho(e.target.value)}
            data-testid="site-tags-ask-who">
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label className="kstw-k" data-testid="site-tags-ask-photos">
            <input type="checkbox" checked={withPhotos}
              onChange={(e) => setWithPhotos(e.target.checked)} />
            {' Include the photos'}
          </label>
          {askProblem ? (
            <p className="kstw-note" data-testid="site-tags-ask-error">{askProblem}</p>
          ) : null}
          <button type="button" className="kstw-btn kstw-btn--brand kstw-wide"
            disabled={!people.length}
            onClick={sendAsk} data-testid="site-tags-ask-send">Send</button>
          <button type="button" className="kstw-btn kstw-wide"
            onClick={() => setAsking(false)}
            data-testid="site-tags-ask-close">Never mind</button>
        </div>
      ) : null}

      {telling && tag ? (
        <TellSomeoneSheet about={{ type: 'site_tag', id: tag.id }}
          onClose={() => { setTelling(false); setScreen('done'); }} />
      ) : null}

    </div>
  );
}
